import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

const emptyResult = (): string => "";

export default function HonourCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      name: "",
      season: "",
      results: [emptyResult()] as string[],
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      sort_order: Yup.string().required("Sort Order is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        season: values.season || "",
        results: values.results.filter((result) => result.trim() !== ""),
        sort_order: values.sort_order,
        status: values.isActive === "Active",
      };

      try {
        let response;
        if (isEdit) {
          response = await request.put("honours/" + id, payload);
        } else {
          response = await request.post("honours/create", payload);
        }

        if (response?.data?.status) {
          toast.success(
            isEdit
              ? "Honour updated successfully!"
              : "Honour created successfully!",
          );
          navigate("/admin/honours");
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  const handleAddResult = () => {
    formik.setFieldValue("results", [...formik.values.results, emptyResult()]);
  };

  const handleRemoveResult = (index: number) => {
    const results = [...formik.values.results];
    results.splice(index, 1);
    formik.setFieldValue("results", results.length ? results : [emptyResult()]);
  };

  const handleResultChange = (index: number, value: string) => {
    const results = [...formik.values.results];
    results[index] = value;
    formik.setFieldValue("results", results);
  };

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`honours/${id}`);
          const item = response.data.data;
          const results = Array.isArray(item.results)
            ? item.results.length
              ? item.results.map((result: string) => String(result))
              : [emptyResult()]
            : [emptyResult()];

          formik.setValues({
            name: item.name || "",
            season: item.season || "",
            results,
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Honour" : "Create New Honour"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/honours"),
              }}
              primaryButton={{
                label: isEdit ? "Update Honour" : "Create Honour",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="name"
                    label="Name"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="season"
                    label="Season"
                    type="text"
                    formik={formik}
                    placeholder="e.g. 2025–26"
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="sort_order"
                    label="Sort Order"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box sx={{ marginTop: "24px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: "#1a1f3a" }}>
                    Results
                  </Typography>
                  <IconButton
                    onClick={handleAddResult}
                    size="small"
                    sx={{
                      color: "#667eea",
                      border: "1px solid #667eea",
                      borderRadius: "8px",
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                {formik.values.results.map((result, index) => (
                  <Box key={index} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <MuiTextField
                          fullWidth
                          size="small"
                          label={`Result ${index + 1}`}
                          value={result}
                          onChange={(event) =>
                            handleResultChange(index, event.target.value)
                          }
                          sx={{ mb: 0 }}
                        />
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveResult(index)}
                        size="small"
                        sx={{ color: "#ef4444", alignSelf: "center" }}
                        title="Remove result"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
